/**
 * Re-fetch when the person comes back to the tab.
 *
 * Data goes stale while a page sits open: a reviewer approves a take, a
 * contributor's balance moves, new work lands in a queue. Reloading the page
 * would fix it, but on the recording screen a reload destroys an unsent take
 * — so refreshing has to happen in place.
 *
 * Coming back to the tab is the moment that matters. Someone who has been
 * away in another window is exactly the person looking at stale numbers, and
 * it costs one request at the moment they start paying attention again.
 *
 * `enabled` is the safety catch: pass false while the person is mid-task, so
 * a refresh can never pull the card out from under them.
 */
import { useEffect, useRef } from "react";

interface Options {
  /** False while the screen is mid-task; no refresh will fire. */
  enabled?: boolean;
  /**
   * Ignore quick alt-tabs. Only refresh if the tab was hidden at least this
   * long, so flicking to another window and back does not spam the API.
   */
  minAwayMs?: number;
  /** Optional polling, for a queue somebody is actively working. */
  intervalMs?: number;
}

export function useRefreshOnFocus(
  refresh: () => void,
  { enabled = true, minAwayMs = 15_000, intervalMs }: Options = {},
) {
  // Held in refs so the listeners always see the current values without
  // being torn down and rebuilt on every render.
  const refreshRef = useRef(refresh);
  const enabledRef = useRef(enabled);

  useEffect(() => {
    refreshRef.current = refresh;
    enabledRef.current = enabled;
  }, [refresh, enabled]);

  useEffect(() => {
    let hiddenAt: number | null = document.hidden ? Date.now() : null;

    const maybeRefresh = () => {
      if (!enabledRef.current) return;
      if (hiddenAt !== null && Date.now() - hiddenAt < minAwayMs) return;
      hiddenAt = null;
      refreshRef.current();
    };

    const onVisibility = () => {
      if (document.hidden) {
        hiddenAt = Date.now();
        return;
      }
      maybeRefresh();
    };

    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("focus", maybeRefresh);

    let timer: number | undefined;
    if (intervalMs) {
      timer = window.setInterval(() => {
        // Never poll a tab nobody is looking at.
        if (!document.hidden && enabledRef.current) refreshRef.current();
      }, intervalMs);
    }

    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("focus", maybeRefresh);
      if (timer) window.clearInterval(timer);
    };
  }, [minAwayMs, intervalMs]);
}
